import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';
import React from 'react';

import {ORGANIZATION_DEFAULT_SCHEMA_COLLABORATORS_CONFIGURATION_FRAGMENT} from 'auth/DefaultSchemaCollaboratorsConfiguration';
import * as BenchlingUtil from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import NoOwnersSchemaMessage from 'schemas/NoOwnersSchemaMessage';
import SchemaSettingsExportDropdown from 'schemas/SchemaSettingsExportDropdown';
import {SCHEMA_TYPES} from 'schemas/spec/SchemaConfigConstants';
import {getSchemaOwnerChoices} from 'schemas/spec/SchemaSpecUtil';
import SettingsPageContainer from 'settings/SettingsPageContainer';
import ExistingStudySchemaEditor from 'studies/study-schemas/ExistingStudySchemaEditor';
import StudySchemaCreateFlow from 'studies/study-schemas/StudySchemaCreateFlow';
import {getStudySchemaListingURL} from 'studies/study-schemas/StudySchemaDAO';
import HorizontalThrobber, {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {StudySchemaEditorQuery} from './__generated/StudySchemaEditorQuery';
import {StudySchemaQuery} from './__generated/StudySchemaQuery';

type MaybeInaccessibleStudySchema = StudySchemaQuery['studySchema'];

type StudySchema = ExtractByTypename<MaybeInaccessibleStudySchema, 'StudySchemaNode'>;

type OrgWithDefaults = NonNullable<StudySchemaEditorQuery['currentTenantOrganizations']>[number];

export const STUDY_SCHEMA_EDITOR_QUERY: DocumentNode = gql`
  query StudySchemaEditorQuery @skipAudit {
    currentTenantOrganizations {
      ... on OrganizationNode {
        ...OrganizationDefaultSchemaCollaboratorsConfiguration
      }
    }
  }
  ${ORGANIZATION_DEFAULT_SCHEMA_COLLABORATORS_CONFIGURATION_FRAGMENT}
`;

export const STUDY_SCHEMA_QUERY: DocumentNode = gql`
  query StudySchemaQuery($schemaId: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    studySchema(id: $schemaId) {
      ... on StudySchemaNode {
        id
        name
        nameTemplates {
          ... on NameTemplateNode {
            templateType
            nameTemplateParts {
              ... on NameTemplatePartNode {
                datetimeFormat
                fieldId
                text
                type
                displayName
                exampleValue
              }
            }
          }
        }
        prefix
        sqlIdentifier
        isDesignApprovalRequired
        studyType
        fields: fieldsJson
        allFields: allFieldsJson
        entryTemplates {
          ... on TemplateNode {
            id
            name
          }
          ...InaccessibleInfoGraphQL
        }
        organization {
          id
          name
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface StudySchemaEditorProps {
  organizations: Array<OrgWithDefaults>;
  schema?: StudySchema;
  refetch: () => void;
}

export const StudySchemaEditor = (props: StudySchemaEditorProps): JSX.Element => {
  const ownerChoices = getSchemaOwnerChoices();

  if (ownerChoices.length === 0) {
    return <NoOwnersSchemaMessage pluralName="study schemas" />;
  }
  return (
    <SettingsPageContainer
      onClickTitle={() => {
        BenchlingUtil.navigate(null, getStudySchemaListingURL());
      }}
      subtitle={props.schema ? props.schema.name : 'Create Study Schema'}
      title="Study Schemas"
      toolbar={
        props.schema && (
          <SchemaSettingsExportDropdown
            schemaId={props.schema.id}
            schemaType={SCHEMA_TYPES.STUDY as unknown as ModelTypeConstants}
          />
        )
      }
    >
      {props.schema ? (
        <ExistingStudySchemaEditor
          schema={props.schema}
          ownerChoices={ownerChoices}
          refetch={props.refetch}
        />
      ) : (
        <StudySchemaCreateFlow
          organizations={props.organizations}
          ownerChoices={ownerChoices}
          schemaPermissions={null}
        />
      )}
    </SettingsPageContainer>
  );
};

interface QueryingStudySchemaEditorProps {
  schemaId?: string;
}
const QueryingStudySchemaEditor = ({schemaId}: QueryingStudySchemaEditorProps): JSX.Element => (
  <BenchlingQuery<StudySchemaQuery>
    query={STUDY_SCHEMA_QUERY}
    renderLoading={() => <CenteredHorizontalThrobber />}
    variables={{schemaId}}
    skip={schemaId == null}
    fetchPolicy="cache-and-network"
  >
    {(data, {refetch: studySchemaRefetch}) => {
      // BenchlingQuery bug: skippable queries will have "data === undefined"
      const maybeSchema: MaybeInaccessibleStudySchema | undefined = data?.studySchema;

      if (isInaccessible(maybeSchema)) {
        notifications.error('You do not have access to this study schema.', {
          id: maybeSchema.invalidId,
        });
        return <HorizontalThrobber />;
      }

      return (
        <BenchlingQuery<StudySchemaEditorQuery>
          query={STUDY_SCHEMA_EDITOR_QUERY}
          renderLoading={() => <CenteredHorizontalThrobber />}
        >
          {({currentTenantOrganizations}, {refetch: studySchemaEditorRefetch}) => (
            <StudySchemaEditor
              organizations={currentTenantOrganizations}
              schema={maybeSchema ?? undefined}
              refetch={() => {
                studySchemaRefetch();
                studySchemaEditorRefetch();
              }}
            />
          )}
        </BenchlingQuery>
      );
    }}
  </BenchlingQuery>
);
export default QueryingStudySchemaEditor;
